/**
 * ============================================================================
 * KbPageEditorPage — Editor completo de página KB
 * ----------------------------------------------------------------------------
 * CHANGELOG:
 *   RELEASE: 2026-07-07 v1.2.0 - F1-Fix: Tiptap com blocks, autosave 5s,
 *            fluxo editorial (draft/review/decision), sidebar árvore,
 *            painel de governança, timeline de versões, review panel.
 *   RELEASE: 2026-07-07 v1.1.0 - F3 Bloco 1/3: Reindexar/Embed + badge status.
 *   RELEASE: 2026-07-07 v1.0.0 - Editor simples de markdown.
 * ============================================================================
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft, FileText, Database,
  PanelLeftClose, PanelLeftOpen, PanelRightClose, PanelRightOpen,
  Eye, Pencil, History,
} from "lucide-react";
import { useKbPage, useUpdateKbPage, usePublishKbPage } from "@/hooks/kb/useKb";
import {
  useIngestKbPage, useEmbedKbChunks, useKbIngestionStatus, useKbEmbeddingConfig,
} from "@/hooks/kb/useKbIngest";
import { useKbSaveDraft, useKbSubmitForReview } from "@/hooks/kb/useKbEditorial";
import { useKnowledgePermissions } from "@/hooks/kb/useKnowledgePermissions";
import { KbTiptapEditor } from "@/components/kb/editor/KbTiptapEditor";
import { KbReviewPanel } from "@/components/kb/editor/KbReviewPanel";
import { KbGovernanceSidebar } from "@/components/kb/editor/KbGovernanceSidebar";
import { KbPageReader } from "@/components/kb/KbPageReader";
import { KbTreeSidebar } from "@/components/kb/KbTreeSidebar";
import { KbOutlinePanel } from "@/components/kb/editor/KbOutlinePanel";
import { KbPageLinkPicker } from "@/components/kb/editor/KbPageLinkPicker";
import { KbPageLinkHydrator } from "@/components/kb/editor/KbPageLinkHydrator";
import { KbPageActionsMenu } from "@/components/kb/editor/KbPageActionsMenu";
import { KbVersionsDialog } from "@/components/kb/editor/KbVersionsDialog";
import { KbLinkEditorDialog } from "@/components/kb/editor/KbLinkEditorDialog";
import {
  setPageLinkPickerHandler,
  setLinkEditorHandler,
  type LinkEditorInitial,
  type LinkEditorResult,
} from "@/lib/kb/pageLinkPicker";
import type { KbLinkSearchResult } from "@/hooks/kb/useKbSearchPagesForLink";
import { tiptapToMarkdown, markdownToTiptap } from "@/lib/kb/markdown";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { toast } from "sonner";

export default function KbPageEditorPage() {
  const { spaceId, pageId } = useParams();
  const { data: page, isLoading } = useKbPage(pageId);
  const updatePage = useUpdateKbPage();
  const publishPage = usePublishKbPage();
  const saveDraft = useKbSaveDraft();
  const submitReview = useKbSubmitForReview();
  const ingestPage = useIngestKbPage();
  const embedChunks = useEmbedKbChunks();
  const { data: embedConfig } = useKbEmbeddingConfig();
  const { data: ingestionRows } = useKbIngestionStatus(spaceId);
  const perms = useKnowledgePermissions();

  const pageStatus = useMemo(
    () => ingestionRows?.find((r) => r.page_id === pageId) ?? null,
    [ingestionRows, pageId],
  );

  const [title, setTitle] = useState("");
  const [contentJson, setContentJson] = useState<any>(null);
  const [classification, setClassification] = useState<string>("internal");
  const [reviewCycleDays, setReviewCycleDays] = useState<number>(180);
  const [dirty, setDirty] = useState(false);
  const [showTree, setShowTree] = useState(true);
  // Estados do novo header GitBook: modo (edit/preview), modal de versões,
  // sheet de governança — todos controlados a partir dos ícones do header.
  const [previewMode, setPreviewMode] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem("kb:editor:mode") === "preview";
  });
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [governanceOpen, setGovernanceOpen] = useState(false);
  useEffect(() => {
    try { window.localStorage.setItem("kb:editor:mode", previewMode ? "preview" : "edit"); } catch { /* ignore */ }
  }, [previewMode]);
  // Outline (índice de headings) — persistido em localStorage.
  const [showOutline, setShowOutline] = useState<boolean>(() => {
    if (typeof window === "undefined") return true;
    const v = window.localStorage.getItem("kb:outline:open");
    return v === null ? true : v === "1";
  });
  useEffect(() => {
    try { window.localStorage.setItem("kb:outline:open", showOutline ? "1" : "0"); } catch { /* ignore */ }
  }, [showOutline]);

  // Picker de link entre artigos — controlado por callback global (bridge com slash menu).
  const [pickerOpen, setPickerOpen] = useState(false);
  const pickerCbRef = useRef<((page: KbLinkSearchResult) => void) | null>(null);

  useEffect(() => {
    setPageLinkPickerHandler((onPick) => {
      pickerCbRef.current = onPick;
      setPickerOpen(true);
    });
    return () => setPageLinkPickerHandler(null);
  }, []);

  const handlePickerPick = useCallback((page: KbLinkSearchResult) => {
    pickerCbRef.current?.(page);
    pickerCbRef.current = null;
  }, []);

  // Editor de link (padrão GitBook) — bridge global compartilhado pelo
  // KbTextBubbleMenu, KbLinkHoverCard e kbBlockCatalog.
  const [linkEditorOpen, setLinkEditorOpen] = useState(false);
  const [linkEditorInitial, setLinkEditorInitial] = useState<LinkEditorInitial>({ kind: "none" });
  const linkEditorCbRef = useRef<((r: LinkEditorResult) => void) | null>(null);

  useEffect(() => {
    setLinkEditorHandler((initial, onSubmit) => {
      linkEditorCbRef.current = onSubmit;
      setLinkEditorInitial(initial);
      setLinkEditorOpen(true);
    });
    return () => setLinkEditorHandler(null);
  }, []);

  const handleLinkEditorSubmit = useCallback((result: LinkEditorResult) => {
    linkEditorCbRef.current?.(result);
    linkEditorCbRef.current = null;
  }, []);

  // Container rolável do editor via CALLBACK REF (estado, não useRef) — assim
  // o painel Outline re-renderiza quando o elemento entra no DOM. Com useRef
  // puro, o .current só popula após o primeiro paint e o painel jamais
  // recebia o container real, fazendo o clique nos títulos não rolar nada.
  const [scrollEl, setScrollEl] = useState<HTMLDivElement | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const setScrollNode = useCallback((node: HTMLDivElement | null) => {
    scrollRef.current = node;
    setScrollEl(node);
  }, []);
  // content_markdown (páginas legadas ou importadas antes do parser novo),
  // reconstrói o JSON via markdownToTiptap para o editor mostrar formatado.
  useEffect(() => {
    if (page) {
      setTitle(page.title ?? "");
      const existingJson = (page as any).content_json;
      const hasValidJson =
        existingJson &&
        typeof existingJson === "object" &&
        (existingJson.type === "doc") &&
        Array.isArray(existingJson.content) &&
        existingJson.content.length > 0 &&
        // considera "vazio" um doc só com paragraph sem texto
        !(existingJson.content.length === 1 &&
          existingJson.content[0].type === "paragraph" &&
          (!existingJson.content[0].content || existingJson.content[0].content.length === 0));

      const c = hasValidJson
        ? existingJson
        : page.content_markdown
          ? markdownToTiptap(page.content_markdown)
          : null;
      setContentJson(c);
      setClassification(page.data_classification ?? "internal");
      setReviewCycleDays((page as any).review_cycle_days ?? 180);
      setDirty(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page?.id, page?.current_version_number]);

  // Autosave 5s (debounce) — gera markdown a partir do JSON antes de enviar.
  const autosaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!dirty || !pageId) return;
    if (page && page.status !== "draft") return; // só draft
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(() => {
      const md = contentJson ? tiptapToMarkdown(contentJson) : "";
      saveDraft.mutate({
        page_id: pageId,
        title,
        content_json: contentJson,
        content_markdown: md,
      }, { onSuccess: () => setDirty(false) });
    }, 5000);
    return () => { if (autosaveTimer.current) clearTimeout(autosaveTimer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dirty, title, contentJson, pageId]);

  const handleManualSave = async () => {
    if (!pageId) return;
    const md = contentJson ? tiptapToMarkdown(contentJson) : "";
    await saveDraft.mutateAsync({
      page_id: pageId,
      title,
      content_json: contentJson,
      content_markdown: md,
    });
    // Atualiza classificação e ciclo via update_page (não fazem parte de save_draft)
    if (page && (classification !== page.data_classification || reviewCycleDays !== (page as any).review_cycle_days)) {
      await updatePage.mutateAsync({
        page_id: pageId,
        data_classification: classification as any,
      });
    }
    setDirty(false);
    toast.success("Rascunho salvo");
  };

  const handleSubmitReview = async () => {
    if (!pageId) return;
    if (dirty) await handleManualSave();
    await submitReview.mutateAsync({ page_id: pageId });
  };

  const handlePublish = async () => {
    if (!pageId) return;
    if (dirty) await handleManualSave();
    await publishPage.mutateAsync(pageId);
  };

  const handleReindex = async () => {
    if (!pageId) return;
    try {
      const res = await ingestPage.mutateAsync({ pageId, fullReingest: true });
      toast.success(`Reindexação concluída: ${res.chunks_generated} chunks gerados`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Falha ao reindexar");
    }
  };

  const handleEmbed = async () => {
    if (!pageId) return;
    try {
      const res = await embedChunks.mutateAsync({ pageId });
      toast.success(`Embeddings: ${res.embedded} chunks (${res.batches} lote${res.batches === 1 ? "" : "s"})`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Falha ao gerar embeddings");
    }
  };

  if (isLoading) {
    return <div className="p-6 space-y-3"><Skeleton className="h-8 w-64" /><Skeleton className="h-96" /></div>;
  }
  if (!page) return <div className="p-6 text-center text-muted-foreground">Página não encontrada.</div>;

  const isDraft = page.status === "draft";
  const isInReview = page.status === "in_review";

  return (
    <div className="flex h-full min-h-0">
      {/* Sidebar árvore */}
      {showTree && spaceId && (
        <aside className="w-[calc(16rem*1.2)] shrink-0 border-r flex flex-col h-full min-h-0">
          <div className="flex items-center justify-between px-2 py-2 border-b shrink-0">
            <span className="text-xs font-medium text-muted-foreground">Páginas do space</span>
            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setShowTree(false)}>
              <PanelLeftClose className="h-3.5 w-3.5" />
            </Button>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto">
            <KbTreeSidebar spaceId={spaceId} activePageId={pageId} />
          </div>
        </aside>
      )}



      <div
        ref={setScrollNode}
        data-kb-scroll
        className="flex-1 flex flex-col min-w-0 gap-3 p-4 md:p-6 overflow-y-auto overflow-x-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 min-w-0">
            {!showTree && (
              <Button variant="ghost" size="icon" onClick={() => setShowTree(true)}>
                <PanelLeftOpen className="h-4 w-4" />
              </Button>
            )}
            <Button variant="ghost" size="icon" asChild>
              <Link to={`/tenant/kb/${spaceId}`}><ArrowLeft className="h-4 w-4" /></Link>
            </Button>
            <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
            <div className="text-xs text-muted-foreground">v{page.current_version_number ?? "—"}</div>
            <Badge variant="outline" className="text-[10px]">{page.status}</Badge>
            {dirty && <Badge className="text-[10px]" variant="secondary">Não salvo</Badge>}
            {pageStatus && (
              <Badge variant="outline" className="text-[10px] gap-1">
                <Database className="h-3 w-3" />
                {pageStatus.chunks_count} chunk{pageStatus.chunks_count === 1 ? "" : "s"}
                {embedConfig && <span className="text-muted-foreground ml-1">· {embedConfig.model_slug}</span>}
              </Badge>
            )}
          </div>

          <div className="flex items-center gap-1 flex-wrap">
            {/* Alterna Editar/Visualizar (substitui aba antiga) */}
            <Button
              size="icon"
              variant={previewMode ? "secondary" : "ghost"}
              onClick={() => setPreviewMode((v) => !v)}
              title={previewMode ? "Voltar para edição" : "Pré-visualizar"}
              className="h-8 w-8"
            >
              {previewMode ? <Pencil className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </Button>
            {/* Abre modal de versões/diff (substitui aba antiga) */}
            <Button
              size="icon"
              variant="ghost"
              onClick={() => setVersionsOpen(true)}
              title="Histórico de versões"
              className="h-8 w-8"
            >
              <History className="h-4 w-4" />
            </Button>
            {/* Toggle do Outline */}
            <Button
              size="icon"
              variant="ghost"
              onClick={() => setShowOutline((v) => !v)}
              title={showOutline ? "Ocultar índice" : "Mostrar índice"}
              className="h-8 w-8"
            >
              {showOutline ? <PanelRightClose className="h-4 w-4" /> : <PanelRightOpen className="h-4 w-4" />}
            </Button>
            {/* Kebab de ações do documento (salvar/publicar/embed/etc.) */}
            <KbPageActionsMenu
              status={page.status}
              canEdit={perms.canEdit}
              canPublish={perms.canPublish}
              hasChunks={!!pageStatus && pageStatus.chunks_count > 0}
              isDirty={dirty}
              isSaving={saveDraft.isPending}
              isSubmittingReview={submitReview.isPending}
              isPublishing={publishPage.isPending}
              isReindexing={ingestPage.isPending}
              isEmbedding={embedChunks.isPending}
              onSaveDraft={handleManualSave}
              onSubmitReview={handleSubmitReview}
              onPublish={handlePublish}
              onReindex={handleReindex}
              onEmbed={handleEmbed}
              onOpenGovernance={() => setGovernanceOpen(true)}
            />
            {/* Governance drawer (controlado pelo kebab) */}
            <Sheet open={governanceOpen} onOpenChange={setGovernanceOpen}>
              <SheetContent
                side="right"
                className="w-full sm:max-w-[560px] p-0 flex flex-col gap-0"
              >
                <SheetHeader className="px-6 pt-6 pb-4 border-b space-y-1 text-left">
                  <SheetTitle className="text-lg font-semibold">Governança &amp; versões</SheetTitle>
                  <SheetDescription className="text-sm text-muted-foreground">
                    Classificação, ciclo de revisão, taxonomias, acessos e histórico da página.
                  </SheetDescription>
                </SheetHeader>
                <div className="flex-1 overflow-y-auto px-6 py-5">
                  <KbGovernanceSidebar
                    pageId={pageId!}
                    classification={classification}
                    reviewCycleDays={reviewCycleDays}
                    lastReviewedAt={(page as any).last_reviewed_at}
                    onClassificationChange={(v) => { setClassification(v); setDirty(true); }}
                    onReviewCycleChange={(v) => { setReviewCycleDays(v); setDirty(true); }}
                    canManageClassification={perms.canManageClassification || perms.isAdmin}
                  />
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>

        {/* Título */}
        <Input value={title}
          onChange={(e) => { setTitle(e.target.value); setDirty(true); }}
          placeholder="Título da página"
          className="text-lg font-medium"
          maxLength={200}
          disabled={!isDraft || !perms.canEdit || previewMode} />

        {/* Painel de revisão se estiver in_review */}
        {isInReview && (
          <KbReviewPanel pageId={pageId!} canReview={perms.canReview || perms.isAdmin} />
        )}

        {/* Editor ou Reader (alternado pelo ícone de olho no header) */}
        {previewMode ? (
          <div className="rounded-md border p-6 bg-background">
            <KbPageReader contentJson={contentJson} fallbackText="Nada para pré-visualizar ainda." />
          </div>
        ) : (
          <KbTiptapEditor
            value={contentJson}
            onChange={(json) => { setContentJson(json); setDirty(true); }}
            editable={isDraft && perms.canEdit}
            placeholder="Comece a escrever… (digite / para abrir o menu de blocos)"
          />
        )}
      </div>

      {/* Sidebar direita — Outline (índice de headings) */}
      {showOutline && (
        <aside className="hidden lg:flex w-64 shrink-0 border-l h-full min-h-0">
          <KbOutlinePanel
            contentJson={contentJson}
            scrollContainer={scrollEl}
            onClose={() => setShowOutline(false)}
          />
        </aside>
      )}

      {/* Hydrator dos wiki-links no editor (títulos ao vivo, navegação SPA, badges) */}
      <KbPageLinkHydrator containerRef={scrollRef} observeKey={contentJson} />

      {/* Picker legado (Slash /link para artigo) */}
      <KbPageLinkPicker
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        currentSpaceId={spaceId ?? null}
        onPick={handlePickerPick}
      />

      {/* Editor completo de link (padrão GitBook: URL externa OU wiki-link) */}
      <KbLinkEditorDialog
        open={linkEditorOpen}
        onOpenChange={setLinkEditorOpen}
        currentSpaceId={spaceId ?? null}
        initial={linkEditorInitial}
        onSubmit={handleLinkEditorSubmit}
      />

      {/* Modal de histórico de versões + diff (abre pelo ícone History) */}
      <KbVersionsDialog
        open={versionsOpen}
        onOpenChange={setVersionsOpen}
        pageId={pageId}
        pageTitle={title}
      />
    </div>
  );
}
